/**
 * Helpers for downloading a legal document PDF in webview / in-app-browser contexts.
 *
 * In-app browsers do not support `window.print()` on iframe content, so legal documents
 * are offered as a PDF download instead. Where the platform supports the Web Share API
 * with files (e.g. iOS), we share the file so the user can save it via the native sheet;
 * otherwise we fall back to a regular anchor download.
 */

/**
 * Attempt to share the PDF via the Web Share API. Returns `true` when the share flow was
 * handled (including a user-initiated cancel), and `false` when sharing is unavailable so
 * the caller can fall back to a direct download.
 */
export const tryShareFile = async (blob: Blob, filename: string): Promise<boolean> => {
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') {
    return false
  }

  const file = new File([blob], filename, { type: 'application/pdf' })
  if (!navigator.canShare({ files: [file] })) {
    return false
  }

  try {
    await navigator.share({ files: [file] })
    return true
  } catch (error) {
    // The user dismissing the native share sheet is not a failure we should fall back from.
    if (error instanceof DOMException && error.name === 'AbortError') {
      return true
    }
    return false
  }
}

/** Trigger a direct browser download of the PDF blob via a temporary anchor element. */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/** Share the PDF when supported, otherwise fall back to a direct download. */
export const downloadOrSharePdf = async (blob: Blob, filename: string): Promise<void> => {
  const shared = await tryShareFile(blob, filename)
  if (!shared) {
    triggerBlobDownload(blob, filename)
  }
}
